import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  MdOutlineUploadFile, MdAdd, MdLink, MdDeleteOutline,
  MdOutlineKeyboardArrowDown, MdOutlineKeyboardArrowUp,
  MdClose, MdCloudUpload, MdOutlineDescription, MdDownload, MdRefresh, MdEdit
} from 'react-icons/md';
import { HiOutlineGlobeAlt } from 'react-icons/hi';
import { IoShieldCheckmarkOutline } from 'react-icons/io5';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import LLMApiService from '@minohealth/common/services/LLMApiService';
import { showErrorMessage, showSuccessMessage } from '@minohealth/common/utils/notifiers';
import { showConfirm } from '@minohealth/common/utils/confirmers';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import './style.css';

function HealthSystemIntegrations({ onIntegrationsChange } = {}) {
  const { user } = useSelector(state => state.app.datastate);
  const apiService = useMemo(() => LLMApiService(user?.userToken), [user?.userToken]);

  // UI state
  const [activeTab, setActiveTab] = useState('url');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Data state
  const [integrations, setIntegrations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [togglingIds, setTogglingIds] = useState(new Set());
  const [expandedId, setExpandedId] = useState(null);
  const [editingCredentials, setEditingCredentials] = useState(null);
  const [isSavingCredentials, setIsSavingCredentials] = useState(false);

  // Form state
  const [ehrName, setEhrName] = useState('');
  const [url, setUrl] = useState('');
  const [files, setFiles] = useState([]);
  const [authInstructions, setAuthInstructions] = useState('');
  const [orgId, setOrgId] = useState('');


  // Track active SSE polling controllers
  const pollingControllers = useRef({});

  // ─── Fetch integrations on mount ────────────────────────────
  useEffect(() => {
    fetchIntegrations();
    return () => {
      // Cleanup: abort all active polls on unmount
      Object.values(pollingControllers.current).forEach(ctrl => ctrl.abort());
    };
  }, []);

  // ─── Start polling for pending integrations when list changes ──
  useEffect(() => {
    integrations.forEach(integration => {
      if (
        integration.status === 'pending' &&
        integration.jobId &&
        !pollingControllers.current[integration.jobId]
      ) {
        startPolling(integration.jobId);
      }
    });
  }, [integrations]);

  async function fetchIntegrations() {
    setIsLoading(true);
    try {
      const { data } = await apiService.getIntegrations();
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setIntegrations(list);
      
      // Notify parent of changes
      onIntegrationsChange?.();
    } catch (error) {
      showErrorMessage(
        error?.response?.data?.message ?? 'Failed to load integrations'
      );
    } finally {
      setIsLoading(false);
    }
  }

  function startPolling(jobId) {
    const controller = apiService.pollIntegrationStatus(jobId, {
      onProgress: (_text) => {
        // Could update a per-integration progress message here
      },
      onResult: (result) => {
        const status = result?.status;
        const isTerminal = status === 'success' || status === 'error';
        
        if (isTerminal) {
          delete pollingControllers.current[jobId];
        }
        
        // Convert "progress" status to "pending" for consistent display
        const displayStatus = status === 'progress' ? 'pending' : (status ?? 'success');
        
        // Update the integration in the list
        setIntegrations(prev =>
          prev.map(integ =>
            integ.jobId === jobId
              ? { ...integ, ...result, status: displayStatus }
              : integ
          )
        );
        
        // Notify parent of status change
        if (isTerminal) {
          onIntegrationsChange?.();
        }
      },
      onError: (err) => {
        delete pollingControllers.current[jobId];
        console.error('Polling error for job', jobId, err);
      }
    });
    pollingControllers.current[jobId] = controller;
  }

  // ─── Form submit ─────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ehrName.trim()) return;

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('ehr_name', ehrName.trim());

      if (activeTab === 'url') {
        if (!url.trim()) return;
        formData.append('url', url.trim());
      } else {
        if (files.length === 0) return;
        files.forEach(file => formData.append('files', file));
      }

      if (authInstructions.trim()) {
        formData.append('auth_instructions', authInstructions.trim());
      }
      if (orgId.trim()) {
        formData.append('org_id', orgId.trim());
      }


      const { data } = await apiService.addIntegration(formData);

      showSuccessMessage('Health tool connected! Processing may take a moment.');

      // Reset form
      setEhrName('');
      setUrl('');
      setFiles([]);
      setAuthInstructions('');
      setOrgId('');

      setShowAdvanced(false);

      // Refresh integrations from backend
      await fetchIntegrations();

      // If a jobId came back, start polling it
      if (data?.job_id) {
        startPolling(data.job_id);
      }
      
      // Notify parent of addition
      onIntegrationsChange?.();
    } catch (error) {
      showErrorMessage(
        error?.response?.data?.message ?? 'Failed to connect health tool. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Delete ──────────────────────────────────────────────────
  const handleDelete = async (integration) => {
    const identifier = integration._id;
    if (!identifier) return;

    try {
      await showConfirm(`Are you sure you want to disconnect "${integration.name}"?`);
    } catch {
      return; // User cancelled
    }

    setDeletingIds(prev => new Set(prev).add(identifier));
    try {
      await apiService.deleteIntegration(identifier);
      setIntegrations(prev => prev.filter(i => i._id !== identifier));
      showSuccessMessage('Health tool disconnected.');
      
      // Notify parent of deletion
      onIntegrationsChange?.();
    } catch (error) {
      showErrorMessage(
        error?.response?.data?.message ?? 'Failed to disconnect health tool.'
      );
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(identifier);
        return next;
      });
    }
  };

  // ─── Toggle integration ──────────────────────────────────────
  const handleToggleEnabled = async (integration) => {
    const identifier = integration._id;
    if (!identifier) return;

    const newEnabled = !integration.enabled;
    setTogglingIds(prev => new Set(prev).add(identifier));

    try {
      await apiService.toggleIntegrationStatus(identifier, newEnabled);
      setIntegrations(prev =>
        prev.map(integ =>
          integ._id === identifier
            ? { ...integ, enabled: newEnabled }
            : integ
        )
      );
      onIntegrationsChange?.();
    } catch (error) {
      showErrorMessage(
        error?.response?.data?.message ?? 'Failed to update health tool.'
      );
    } finally {
      setTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(identifier);
        return next;
      });
    }
  };

  // ─── Retry integration ───────────────────────────────────────
  const handleRetry = async (integration) => {
    const identifier = integration.uniqueId || integration._id;
    if (!identifier) return;

    try {
      const { data } = await apiService.retryFailedIntegration(identifier);
      
      // Update status to pending - polling will handle the rest
      setIntegrations(prev =>
        prev.map(integ =>
          (integ.uniqueId || integ._id) === identifier
            ? { ...integ, status: 'pending', error: undefined, jobId: data.job_id }
            : integ
        )
      );

      showSuccessMessage('Retrying integration...');
      
      // Start polling with new job_id
      if (data?.job_id) {
        startPolling(data.job_id);
      }
      
      onIntegrationsChange?.();
    } catch (error) {
      showErrorMessage(
        error?.response?.data?.message ?? 'Failed to retry integration.'
      );
    }
  };

  // ─── Expand / Credentials ────────────────────────────────────
  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
    setEditingCredentials(null);
  };

  const handleSaveCredentials = async (integration) => {
    if (!editingCredentials) return;

    setIsSavingCredentials(true);
    try {
      await apiService.updateIntegrationCredentials(integration._id, editingCredentials);
      setIntegrations(prev =>
        prev.map(integ =>
          integ._id === integration._id
            ? { ...integ, credentials: { ...editingCredentials } }
            : integ
        )
      );
      setEditingCredentials(null);
      showSuccessMessage('Credentials updated.');
    } catch (error) {
      showErrorMessage(
        error?.response?.data?.message ?? 'Failed to update credentials.'
      );
    } finally {
      setIsSavingCredentials(false);
    }
  };

  // ─── File helpers ────────────────────────────────────────────
  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...droppedFiles]);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
    e.target.value = '';
  };

  // ─── Derived data ────────────────────────────────────────────
  const getSourceType = (integration) => integration?.source?.type ?? 'url';
  const getSourceUrl = (integration) => integration?.source?.url ?? '';
  const getSourceFileCount = (integration) => integration?.source?.files?.length ?? 0;
  const getSourceFiles = (integration) => integration?.source?.files ?? [];

  const handleDownloadAll = async (integration) => {
    const sourceType = getSourceType(integration);
    if (sourceType === 'url') {
      window.open(getSourceUrl(integration), '_blank');
      return;
    }
    const files = getSourceFiles(integration);
    for (let i = 0; i < files.length; i++) {
      try {
        const presignedUrl = await apiService.getPresignedDownloadUrl(files[i].url);
        const a = document.createElement('a');
        a.href = presignedUrl;
        a.download = files[i].name;
        a.click();
      } catch {
        showErrorMessage(`Failed to download ${files[i].name}`);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 30) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return '';
    }
  };

  // ─── Render ──────────────────────────────────────────────────
  return (
    <div className="integrations-container">
      {/* Header */}
      <div className="integrations-header">
        <div className="integrations-header-top">
          <div className="integrations-header-icon">
            <HiOutlineGlobeAlt />
          </div>
          <div>
            <h1>Health Tool Integrations</h1>
            <p>
              Connect your EHR, EMR, or other clinical data sources to enhance AI-powered assistance with your health tools.
            </p>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="integration-card">
        <div className="integration-card-header">
          <MdAdd className="integration-card-header-icon" />
          <h2 className="integration-card-title">Connect New Health Tool</h2>
        </div>

        <form onSubmit={handleSubmit} className="integration-form">
          {/* System Name */}
          <div className="input-group">
            <label className="input-label">
              Health Tool Name <span className="required-asterisk">*</span>
            </label>
            <input
              type="text"
              required
              value={ehrName}
              onChange={(e) => setEhrName(e.target.value)}
              placeholder="e.g., Epic Systems, Cerner, Athenahealth"
              className="text-input"
              disabled={isSubmitting}
            />
            <span className="input-hint">The name of the health tool or service you want to connect</span>
          </div>

          {/* Source Type Tabs */}
          <div className="input-group">
            <label className="input-label">Documentation Source</label>
            <div className="tab-buttons-container">
              <button
                type="button"
                onClick={() => setActiveTab('url')}
                className={`tab-button ${activeTab === 'url' ? 'active' : ''}`}
                disabled={isSubmitting}
              >
                <MdLink size={18} />
                <span>Provide URL</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('file')}
                className={`tab-button ${activeTab === 'file' ? 'active' : ''}`}
                disabled={isSubmitting}
              >
                <MdOutlineUploadFile size={18} />
                <span>Upload Files</span>
              </button>
            </div>
          </div>

          {/* URL Input */}
          {activeTab === 'url' ? (
            <div className="input-group">
              <label className="input-label">
                System Documentation URL <span className="required-asterisk">*</span>
              </label>
              <div className="url-input-wrapper">
                <MdLink className="url-input-icon" />
                <input
                  type="url"
                  required
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/api/docs"
                  className="text-input url-input"
                  disabled={isSubmitting}
                />
              </div>
              <span className="input-hint">Link to the system's API documentation or reference page</span>
            </div>
          ) : (
            /* File Upload */
            <div className="input-group">
              <label className="input-label">
                Upload Documentation Files <span className="required-asterisk">*</span>
              </label>
              <div
                className={`file-upload-area ${isDragging ? 'dragging' : ''} ${isSubmitting ? 'disabled' : ''}`}
                onClick={() => !isSubmitting && fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="file-upload-icon-wrapper">
                  <MdCloudUpload className="file-upload-icon" />
                </div>
                <p className="file-upload-title">
                  {isDragging ? 'Drop files here...' : 'Drag & drop files here'}
                </p>
                <p className="file-upload-subtitle">or click to browse</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="file-input-hidden"
                  onChange={handleFileSelect}
                  accept=".json,.yaml,.yml,.pdf,.md,.txt"
                  disabled={isSubmitting}
                />
                <div className="file-types-badges">
                  <span className="file-type-badge">JSON</span>
                  <span className="file-type-badge">YAML</span>
                  <span className="file-type-badge">PDF</span>
                  <span className="file-type-badge">Markdown</span>
                </div>
              </div>

              {/* File List */}
              {files.length > 0 && (
                <div className="file-list">
                  {files.map((file, index) => (
                    <div key={index} className="file-item">
                      <div className="file-item-info">
                        <MdOutlineDescription className="file-item-icon" />
                        <span className="file-item-name">{file.name}</span>
                        <span className="file-item-size">
                          {(file.size / 1024).toFixed(1)} KB
                        </span>
                      </div>
                      <button
                        type="button"
                        className="file-item-remove"
                        onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                        aria-label={`Remove ${file.name}`}
                        disabled={isSubmitting}
                      >
                        <MdClose size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Advanced Options */}
          <div className="advanced-section">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="advanced-options-toggle"
            >
              <IoShieldCheckmarkOutline size={16} />
              <span>Advanced Options</span>
              {showAdvanced
                ? <MdOutlineKeyboardArrowUp size={20} />
                : <MdOutlineKeyboardArrowDown size={20} />
              }
            </button>

            {showAdvanced && (
              <div className="advanced-options-container">
                <div className="input-group">
                  <label className="input-label">Authentication Instructions</label>
                  <textarea
                    rows={3}
                    value={authInstructions}
                    onChange={(e) => setAuthInstructions(e.target.value)}
                    placeholder="e.g., Use Bearer token in Authorization header. Token format: Bearer {api_key}"
                    className="text-input textarea-input"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Organization ID / Name</label>
                  <input
                    type="text"
                    value={orgId}
                    onChange={(e) => setOrgId(e.target.value)}
                    placeholder="Optional — used for multi-tenant systems"
                    className="text-input"
                    disabled={isSubmitting}
                  />
                </div>


              </div>
            )}
          </div>

          {/* Submit */}
          <div className="submit-btn-container">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <AiOutlineLoading3Quarters size={16} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <MdAdd size={18} />
                  Connect Health Tool
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Integrations List */}
      <div className="integrations-list-section">
        <div className="integrations-list-header">
          <h2 className="integration-card-title">Connected Tools</h2>
          {!isLoading && <span className="integrations-count">{integrations.length}</span>}
        </div>

        {isLoading ? (
          <div className="integrations-loading">
            <AiOutlineLoading3Quarters size={24} className="spin-icon" />
            <span>Loading health tools...</span>
          </div>
        ) : integrations.length === 0 ? (
          <div className="empty-integrations">
            <div className="empty-integrations-icon-wrapper">
              <HiOutlineGlobeAlt className="empty-integrations-icon" />
            </div>
            <p className="empty-integrations-title">No health tools connected</p>
            <p className="empty-integrations-subtitle">
              Connect your first health tool above to get started
            </p>
          </div>
        ) : (
          <div className="integrations-list">
            {integrations.map((integration) => {
              const sourceType = getSourceType(integration);
              const identifier = integration.uniqueId || integration._id;
              const isDeleting = deletingIds.has(identifier);
              const isToggling = togglingIds.has(identifier);

              const isExpanded = expandedId === integration._id;
              const credentials = integration.credentials ?? {};
              const credentialEntries = Object.entries(credentials);
              const hasCredentials = credentialEntries.length > 0;

              return (
                <div
                  key={integration._id}
                  className={`integration-item ${isDeleting ? 'deleting' : ''} ${isExpanded ? 'expanded' : ''}`}
                >
                  <div
                    className="integration-item-row"
                    onClick={() => toggleExpand(integration._id)}
                  >
                    <div className="integration-item-left">
                      <div className={`integration-type-icon ${sourceType}`}>
                        {sourceType === 'url'
                          ? <MdLink size={20} />
                          : <MdOutlineUploadFile size={20} />
                        }
                      </div>
                      <div className="integration-item-info">
                        <div className="integration-item-header">
                          <span className="integration-item-name">{integration.name}</span>
                          <span className={`status-badge ${integration.status}`}>
                            <span className={`status-indicator ${integration.status}`} />
                            {integration.status}
                          </span>
                        </div>
                        <div className="integration-item-details">
                          {sourceType === 'url' ? (
                            <a
                              href={getSourceUrl(integration)}
                              target="_blank"
                              rel="noreferrer"
                              className="integration-item-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {getSourceUrl(integration)}
                            </a>
                          ) : (
                            <span>{getSourceFileCount(integration)} uploaded file(s)</span>
                          )}
                          {integration.createdAt && (
                            <span className="integration-item-time">
                              • {formatDate(integration.createdAt)}
                            </span>
                          )}
                        </div>
                        {integration.status === 'error' && integration.error?.message && (
                          <div className="integration-error-message">
                            {integration.error.message}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="integration-item-actions" onClick={(e) => e.stopPropagation()}>
                      {integration.status === 'success' && (
                        <div className="integration-toggle-wrapper">
                          <Switch
                            checked={integration.enabled ?? true}
                            onCheckedChange={() => handleToggleEnabled(integration)}
                            disabled={isToggling}
                          />
                        </div>
                      )}
                      {integration.status === 'error' && (
                        <button
                          onClick={() => handleRetry(integration)}
                          className="retry-btn"
                          title="Retry integration"
                          aria-label="Retry integration"
                          disabled={integration.status === 'pending'}
                        >
                          <MdRefresh size={18} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDownloadAll(integration)}
                        className="download-btn"
                        title={getSourceType(integration) === 'url' ? 'Open URL' : 'Download files'}
                        aria-label={getSourceType(integration) === 'url' ? 'Open URL' : 'Download files'}
                      >
                        <MdDownload size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(integration)}
                        className="delete-btn"
                        title="Disconnect health tool"
                        aria-label="Disconnect health tool"
                        disabled={isDeleting}
                      >
                        {isDeleting
                          ? <AiOutlineLoading3Quarters size={16} className="spin-icon" />
                          : <MdDeleteOutline size={18} />
                        }
                      </button>
                      <MdOutlineKeyboardArrowDown
                        size={18}
                        className={`integration-expand-icon ${isExpanded ? 'rotated' : ''}`}
                      />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="integration-credentials">
                      <div className="credentials-header">
                        <span className="credentials-title">Credentials</span>
                        {hasCredentials && editingCredentials === null && (
                          <button
                            className="credentials-edit-btn"
                            onClick={() => setEditingCredentials({ ...credentials })}
                          >
                            <MdEdit size={14} />
                            <span>Edit</span>
                          </button>
                        )}
                      </div>

                      {!hasCredentials ? (
                        <p className="credentials-empty">No credentials configured</p>
                      ) : editingCredentials !== null ? (
                        <>
                          <div className="credentials-list">
                            {credentialEntries.map(([key]) => (
                              <div key={key} className="credential-row">
                                <label className="credential-key">{key}</label>
                                <input
                                  type="text"
                                  className="credential-input"
                                  value={editingCredentials[key] ?? ''}
                                  onChange={(e) =>
                                    setEditingCredentials(prev => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  disabled={isSavingCredentials}
                                />
                              </div>
                            ))}
                          </div>
                          <div className="credentials-actions">
                            <button
                              className="credentials-cancel-btn"
                              onClick={() => setEditingCredentials(null)}
                              disabled={isSavingCredentials}
                            >
                              Cancel
                            </button>
                            <Button
                              size="sm"
                              onClick={() => handleSaveCredentials(integration)}
                              disabled={isSavingCredentials}
                            >
                              {isSavingCredentials ? (
                                <>
                                  <AiOutlineLoading3Quarters size={14} className="animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                'Save'
                              )}
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="credentials-list">
                          {credentialEntries.map(([key, value]) => (
                            <div key={key} className="credential-row">
                              <span className="credential-key">{key}</span>
                              <span className="credential-value">
                                {value ?? ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default HealthSystemIntegrations;
